import { formatCurrency } from '@shared/SharedAccountBody';
import type { StockTransaction } from '@shared/types';

interface PortfolioTransactionsTableProps {
  symbol: string;
  transactions?: StockTransaction[];
  loading?: boolean;
  error?: string | null;
  truncated?: boolean;
  /** Current avg entry — used for simple per-sell realized P/L. */
  avgEntryPrice?: number;
  /** Current held quantity — used to show each fill as a % of total held. */
  heldQty?: number;
}

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatQty(qty: number): string {
  if (Number.isInteger(qty)) return String(qty);
  return qty.toFixed(4).replace(/\.?0+$/, '');
}

const signedCurrency = (v: number) => `${v >= 0 ? '+' : ''}${formatCurrency(v)}`;

const PortfolioTransactionsTable = ({
  symbol,
  transactions,
  loading,
  error,
  truncated,
  avgEntryPrice,
  heldQty,
}: PortfolioTransactionsTableProps) => {
  const showPl = avgEntryPrice !== undefined && avgEntryPrice > 0;
  const showHeldPct = heldQty !== undefined && heldQty > 0;
  const rowPl = (tx: StockTransaction): number | null =>
    showPl && tx.side === 'sell' ? (tx.price - avgEntryPrice!) * tx.qty : null;
  const realizedTotal = (transactions ?? []).reduce(
    (sum, tx) => sum + (rowPl(tx) ?? 0),
    0,
  );

  const numCell = 'px-3 py-[9px] text-right tabular-nums';
  const headCell =
    'sticky top-0 z-[1] bg-[color:#11161d] px-3 py-2.5 text-left text-[11px] uppercase tracking-[0.04em] text-muted-foreground border-b border-border';

  return (
    <section className="mt-5">
      <h3 className="m-0 mb-3 font-display text-[0.95rem] font-semibold text-foreground">
        Transaction history
      </h3>

      {loading && !transactions ? (
        <p className="m-0 text-[13px] text-muted-foreground">Loading transactions…</p>
      ) : error && !transactions ? (
        <p className="m-0 text-[13px] text-muted-foreground">
          Transaction history is unavailable.
        </p>
      ) : !transactions || transactions.length === 0 ? (
        <p className="m-0 text-[13px] text-muted-foreground">No fills found for {symbol}.</p>
      ) : (
        <>
          <div className="max-h-[280px] overflow-y-auto rounded-[10px] border border-border">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr>
                  <th className={headCell}>Date</th>
                  <th className={headCell}>Side</th>
                  <th className={`${headCell} text-right`}>Qty</th>
                  <th className={`${headCell} text-right`}>Price</th>
                  <th className={`${headCell} text-right`}>Value</th>
                  {showPl && <th className={`${headCell} text-right`}>Realized P/L</th>}
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => {
                  const pl = rowPl(tx);
                  return (
                    <tr
                      key={tx.id}
                      className="border-b border-[color:rgba(255,255,255,0.04)] last:border-b-0 hover:bg-[color:rgba(255,255,255,0.03)]"
                    >
                      <td className="whitespace-nowrap px-3 py-[9px] text-muted-foreground">
                        {formatDateTime(tx.time)}
                      </td>
                      <td className="px-3 py-[9px]">
                        <span
                          className={`rounded-[5px] px-[7px] py-0.5 text-[10px] font-bold tracking-[0.04em] ${
                            tx.side === 'buy'
                              ? 'bg-positive text-[color:var(--nq-bg-deep)]'
                              : 'bg-negative text-foreground'
                          }`}
                        >
                          {tx.side === 'buy' ? 'BUY' : 'SELL'}
                        </span>
                      </td>
                      <td className={`${numCell} text-foreground/80`}>
                        <span className="block">{formatQty(tx.qty)}</span>
                        {showHeldPct && (
                          <span className="mt-0.5 block text-[11px] text-foreground/40">
                            {((tx.qty / heldQty!) * 100).toFixed(1)}% of held
                          </span>
                        )}
                      </td>
                      <td className={`${numCell} text-foreground/80`}>
                        {formatCurrency(tx.price)}
                      </td>
                      <td className={`${numCell} text-foreground/80`}>
                        {formatCurrency(tx.notional)}
                      </td>
                      {showPl && (
                        <td
                          className={`${numCell} ${
                            pl === null
                              ? 'text-foreground/30'
                              : pl >= 0
                                ? 'text-positive'
                                : 'text-negative'
                          }`}
                        >
                          {pl === null ? '—' : signedCurrency(pl)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {showPl && (
                <tfoot>
                  <tr>
                    <td
                      colSpan={5}
                      className="sticky bottom-0 border-t border-[color:rgba(255,255,255,0.12)] bg-[color:#11161d] px-3 py-[11px] text-right text-[11px] font-bold uppercase tracking-[0.04em] text-muted-foreground"
                    >
                      Realized to date
                    </td>
                    <td
                      className={`sticky bottom-0 border-t border-[color:rgba(255,255,255,0.12)] bg-[color:#11161d] px-3 py-[11px] text-right font-bold tabular-nums ${
                        realizedTotal >= 0 ? 'text-positive' : 'text-negative'
                      }`}
                    >
                      {signedCurrency(realizedTotal)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {truncated && (
            <p className="mt-2.5 text-xs text-muted-foreground">
              Showing the most recent fills; older transactions may exist.
            </p>
          )}
        </>
      )}
    </section>
  );
};

export default PortfolioTransactionsTable;
